// scheduling/components/NonBillBudgetTab.jsx
// Stage 28F — Non-Billable Budget Hours allocation form.
// Each staffer allocates their annual non-billable / "admin" hour budget
// across categories. FY-bound (May 1 → Apr 30); independent of IDD cycles.
// Save Draft / Submit lifecycle mirrors IDD (no per-event audit).

import { useState, useEffect, useCallback, useMemo } from 'react';
import { fetchNonBillCategories, fetchNonBillMyBudget } from '../services/schedData';
import { saveNonBillDraft, submitNonBill } from '../services/schedApi';
import NonBillBudgetChart from './NonBillBudgetChart';

// FY May 1 → Apr 30. Returns "May 1, YYYY – Apr 30, YYYY+1".
function fmtFiscalYear(fyKey) {
  if (!fyKey) return '';
  const start = parseInt(fyKey, 10);
  return `May 1, ${start} – Apr 30, ${start + 1}`;
}

function fmtNum(v, decimals = 0) {
  if (v == null) return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return n.toFixed(decimals);
}

export default function NonBillBudgetTab({ myStaffId }) {
  const [categories, setCategories] = useState([]);
  const [budgetData, setBudgetData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Per-category edit state. Keys = CategoryKey, values = number string.
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);


  const reload = useCallback(async () => {
    if (!myStaffId) return;
    setLoading(true);
    setError(null);
    try {
      const [cats, mine] = await Promise.all([
        fetchNonBillCategories(),
        fetchNonBillMyBudget(myStaffId),
      ]);
      setCategories(cats || []);
      setBudgetData(mine || null);
      // Seed edits from saved allocations so the inputs reflect current state.
      const seeded = {};
      for (const a of (mine?.allocations || [])) {
        seeded[a.CategoryKey] = String(a.Hours);
      }
      // Pre-fill PTO from PY actual when the user has not yet saved a value
      // for it. PE's EntitlementHours is "remaining entitlement" (often a
      // small number once carryover/used/requests are subtracted), not the
      // user's typical annual PTO usage — so it's a poor default. PY actual
      // is what the user actually took last year, which is the most
      // predictive baseline. User can override.
      const ptoSaved = (mine?.allocations || []).find(a => a.CategoryKey === 'pto');
      if (!ptoSaved) {
        const pyPto = mine?.wipPY?.pto;
        if (pyPto != null && pyPto > 0) {
          seeded['pto'] = String(Math.round(pyPto));
        }
      }
      setEdits(seeded);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [myStaffId]);

  useEffect(() => { reload(); }, [reload]);

  // Build per-category row data combining categories + WIP + saved allocations.
  const rows = useMemo(() => {
    if (!categories.length || !budgetData) return [];
    const wipCY = budgetData.wipCY || {};
    const wipPY = budgetData.wipPY || {};
    return categories.map(c => {
      const key = c.CategoryKey;
      const editVal = edits[key];
      const saved = (budgetData.allocations || []).find(a => a.CategoryKey === key);
      const allocation = editVal != null && editVal !== '' ? Number(editVal) : (saved ? Number(saved.Hours) : 0);
      return {
        key,
        displayName: c.DisplayName,
        description: c.Description,
        allocation,
        cyActual: wipCY[key] || 0,
        pyActual: wipPY[key] || 0,
        savedHours: saved ? Number(saved.Hours) : null,
      };
    });
  }, [categories, budgetData, edits]);

  const totalEffective = budgetData?.totalEffective != null ? Number(budgetData.totalEffective) : null;
  const standardSource = budgetData?.standardSource;
  const allocSum = useMemo(
    () => rows.reduce((s, r) => s + (r.allocation || 0), 0),
    [rows]
  );
  const remaining = totalEffective != null ? Math.round((totalEffective - allocSum) * 100) / 100 : null;
  const overAllocated = totalEffective != null && allocSum > totalEffective + 0.01;
  const underAllocated = totalEffective != null && allocSum < totalEffective - 0.01;
  const exactMatch = totalEffective != null && Math.abs(allocSum - totalEffective) < 0.01;
  const status = budgetData?.budget?.Status || null; // 'draft' | 'submitted' | null
  const isSubmitted = status === 'submitted';
  const fy = budgetData?.fiscalYear || '';

  // True when at least one row's current value differs from its saved value
  // (or has a value where none was saved — e.g. PTO pre-fill or fresh edits).
  // Drives the Discard Changes button's disabled state.
  const hasUnsavedChanges = useMemo(
    () => rows.some(r => edits[r.key] != null && r.savedHours !== r.allocation),
    [rows, edits]
  );

  // NOTE: Expected / Variance / pacing toggle were removed from this page
  // because the data integrity tab is for SETTING the budget once a year
  // (week 1 of FY), not tracking it. Variance against linear is meaningless
  // when CY logged is essentially zero. Year-round tracking lives on the
  // EmployeeProfile → Non-Billable Hours tab where pacing matters.
  //
  // FUTURE: Burn-rate on the profile panel will be upgraded to use firmwide
  // PY daily distribution share aggregated by (Department × LevelCode)
  // cohort — the same cohort key that drives NonBillStandards. Plan +
  // schema sketch for that work lives in the plan file under "Stage 28
  // follow-up — True seasonal burn-rate (deferred)".

  function handleEdit(key, val) {
    if (isSubmitted) return;
    setEdits(prev => ({ ...prev, [key]: val }));
  }

  function handleClearOne(key) {
    setEdits(prev => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }

  // Quick-fill: copy PY Actual into Allocation for every category. Rounded
  // to whole hours (matches the "round to whole" pattern from IDD's PY
  // suggestion). Sum will rarely equal TOTAL exactly — user adjusts before
  // submitting; the validation gate is unchanged.
  function handleApplyPYActual() {
    if (isSubmitted) return;
    if (!window.confirm('Pre-fill all categories with last year\'s actual hours (rounded)? Your existing edits in those rows will be overwritten — you can still tweak before saving or submitting.')) return;
    setEdits(prev => {
      const next = { ...prev };
      for (const r of rows) {
        next[r.key] = String(Math.max(0, Math.round(r.pyActual || 0)));
      }
      return next;
    });
  }

  async function handleSaveDraft() {
    if (isSubmitted) return;
    setSaving(true);
    setError(null);
    try {
      const allocations = rows.map(r => ({ categoryKey: r.key, hours: r.allocation }));
      await saveNonBillDraft({ allocations, fiscalYear: fy });
      await reload();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleSubmit() {
    if (isSubmitted) return;
    if (!exactMatch) {
      setError(`Allocation total ${allocSum.toFixed(2)} must equal your annual budget ${totalEffective?.toFixed(2)} exactly before submitting.`);
      return;
    }
    if (!window.confirm(
      `Submit your non-billable budget for ${fy}?\n\n`
      + `This locks the allocation. Only an admin can reopen it.`
    )) return;
    setSubmitting(true);
    setError(null);
    try {
      const allocations = rows.map(r => ({ categoryKey: r.key, hours: r.allocation }));
      await submitNonBill({ allocations, fiscalYear: fy });
      await reload();
      // Submit still credits 10 tokens server-side (one-time per FY); the
      // wallet badge that used to surface it was shelved June 2026.
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  }

  function handleDiscard() {
    if (isSubmitted) return;
    if (!window.confirm('Discard unsaved changes?')) return;
    const seeded = {};
    for (const a of (budgetData?.allocations || [])) {
      seeded[a.CategoryKey] = String(a.Hours);
    }
    setEdits(seeded);
  }

  if (!myStaffId) {
    return (
      <div className="idd-placeholder" style={{ flex: 1 }}>
        Resolving your staff record…
      </div>
    );
  }
  if (loading) {
    return (
      <div className="sched-home-loading" style={{ flex: 1 }}>
        <div className="sched-home-loading-text">Loading non-billable budget…</div>
      </div>
    );
  }
  if (error && !budgetData) {
    return <div style={{ padding: 24, color: '#DC2626' }}>Error: {error}</div>;
  }
  if (totalEffective == null) {
    return (
      <div className="idd-placeholder" style={{ flex: 1 }}>
        <strong>No standard configured for your department / level.</strong>
        <br />
        Ask an admin to set up a standard or override before allocating your non-billable budget.
      </div>
    );
  }

  return (
    <div className="nonbill-tab" style={{ flex: 1, overflow: 'auto', padding: '16px 20px' }}>
      {/* Page-level info banner — frames the page as a tracking/reference
          tool, not a true "PTO allotment" surface. Users sometimes assume
          changing the PTO row affects their actual leave balance, so this
          context up front avoids confusion. */}
      <div className="nonbill-info-banner">
        <strong>What's this page for?</strong> Set a reference budget for how you plan to spend your annual non-billable
        hours across categories. It's a tool for your own progress tracking through the year — these allocations
        do <em>not</em> change anything in PE or affect billable / PTO balances.
        {myStaffId && (
          <div className="nonbill-info-banner-secondary">
            After you submit, you can review your budget vs. actuals year-round on{' '}
            <a className="nonbill-info-banner-link" href={`/schedule/employee/${myStaffId}?tab=nonbill`}>
              your employee profile → Non-Billable Hours tab
            </a>
            {' '}(or click your photo in the top-right of the page to get there).
          </div>
        )}
      </div>

      {/* Top section: stats + live composition chart side-by-side. Status
          badge floats in the upper-right of the whole block; quick-fill
          button lives near its action target (the alloc card / table). */}
      <div className="nonbill-top">
        <div className="nonbill-top-stats">
          <div className="nonbill-top-headline">
            <div>
              <div className="nonbill-fy-label">
                Fiscal Year {fy} <span className="nonbill-fy-range">({fmtFiscalYear(fy)})</span>
              </div>
              <div className="nonbill-total-line">
                <strong>{fmtNum(totalEffective, 0)}</strong> hrs annual budget
                {standardSource === 'override' && <span className="nonbill-source-chip nonbill-source-chip--override">Admin Override Amount</span>}
                {standardSource === 'standard' && <span className="nonbill-source-chip">dept × level standard</span>}
              </div>
            </div>
            <div className="nonbill-top-status">
              {isSubmitted ? (
                <span className="idd-status-badge idd-status-badge--lg idd-status--confirmed">Submitted ✓</span>
              ) : status === 'draft' ? (
                <span className="idd-status-badge idd-status-badge--lg idd-status--draft">Saved Draft</span>
              ) : (
                <span className="idd-status-badge idd-status-badge--lg idd-status--pending">Pending</span>
              )}
            </div>
          </div>

          {/* Allocation summary — visual progress bar + status indicator. */}
          <div className={`nonbill-alloc-card ${overAllocated ? 'nonbill-alloc-card--over' : exactMatch ? 'nonbill-alloc-card--ok' : ''}`}>
            <div className="nonbill-alloc-headline">
              <span>Allocated: <strong>{fmtNum(allocSum, 2)}</strong> / {fmtNum(totalEffective, 0)} hrs</span>
              {underAllocated && <span className="nonbill-alloc-warn">{fmtNum(remaining, 2)}h remaining</span>}
              {overAllocated && <span className="nonbill-alloc-err">⚠ over by {fmtNum(-remaining, 2)}h</span>}
              {exactMatch && <span className="nonbill-alloc-ok">✓ matches budget</span>}
            </div>
            <div className="nonbill-alloc-progress">
              <div
                className={`nonbill-alloc-progress-fill ${overAllocated ? 'nonbill-alloc-progress-fill--over' : exactMatch ? 'nonbill-alloc-progress-fill--ok' : ''}`}
                style={{ width: `${Math.min(100, totalEffective > 0 ? (allocSum / totalEffective) * 100 : 0)}%` }}
              />
              {overAllocated && (
                /* Bleed marker showing how far past 100% we are */
                <div className="nonbill-alloc-progress-overflow" />
              )}
            </div>
          </div>

          {!isSubmitted && rows.some(r => (r.pyActual || 0) > 0) && (
            <div className="nonbill-quickfill-row">
              <button
                type="button"
                className="idd-btn idd-btn--secondary nonbill-quickfill-btn"
                onClick={handleApplyPYActual}
                disabled={saving || submitting}
                title="Pre-fill every category with last year's actual hours (rounded). You can tweak before saving or submitting."
              >
                ↻ Apply PY Actual
              </button>
              <span className="nonbill-quickfill-hint">
                Pre-fills allocations from last year's actuals; tweak before saving.
              </span>
            </div>
          )}
        </div>

        {/* Live composition donut. Compact size; legend renders inline. */}
        <div className="nonbill-top-chart">
          <NonBillBudgetChart
            categories={categories}
            allocations={rows.reduce((acc, r) => { acc[r.key] = r.allocation; return acc; }, {})}
            totalEffective={totalEffective}
            size={180}
            showLegend={true}
          />
        </div>
      </div>

      {error && <div className="idd-admin-error" style={{ marginTop: 8 }}>{error}</div>}


      {/* Allocation table */}
      <table className="nonbill-table">
        <thead>
          <tr>
            <th>Category</th>
            <th style={{ textAlign: 'right' }}>Allocation</th>
            <th style={{ textAlign: 'right' }}>PY Actual</th>
            <th style={{ textAlign: 'right' }}>CY Logged</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => {
            const dirty = edits[r.key] != null && r.savedHours !== r.allocation;
            return (
              <tr key={r.key} className={dirty ? 'nonbill-row--dirty' : ''}>
                <td title={r.description || ''}>
                  {r.displayName}
                  {r.key === 'pto' && (
                    <div className="nonbill-row-hint" title="This won't affect your real PTO balance — promise!">
                      As nice as it would be to allocate 100% to PTO, this is just a reference to help you track
                      your time through the year — it won't actually change your PTO balance{' '}
                      <span style={{ fontStyle: 'normal', display: 'inline-block' }}>😉</span>
                    </div>
                  )}
                </td>
                <td style={{ textAlign: 'right' }}>
                  <input
                    className="nonbill-input"
                    type="number"
                    min="0"
                    step="1"
                    value={edits[r.key] ?? (r.savedHours != null ? String(r.savedHours) : '')}
                    onChange={e => handleEdit(r.key, e.target.value)}
                    disabled={isSubmitted}
                    placeholder="0"
                  />
                  {dirty && !isSubmitted && (
                    <button
                      type="button"
                      className="nonbill-clear-btn"
                      onClick={() => handleClearOne(r.key)}
                      title="Revert to saved value"
                    >
                      ↺
                    </button>
                  )}
                </td>
                <td style={{ textAlign: 'right', color: '#6B7280' }}>{fmtNum(r.pyActual, 2)}</td>
                <td style={{ textAlign: 'right' }}>{fmtNum(r.cyActual, 2)}</td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr className={`nonbill-totals-row ${overAllocated ? 'nonbill-totals-row--over' : exactMatch ? 'nonbill-totals-row--ok' : ''}`}>
            <td><strong>TOTAL</strong></td>
            <td style={{ textAlign: 'right' }}>
              <strong className={overAllocated ? 'nonbill-alloc-err' : exactMatch ? 'nonbill-alloc-ok' : ''}>
                {fmtNum(allocSum, 2)} / {fmtNum(totalEffective, 0)}
                {overAllocated && ' ⚠'}
                {exactMatch && ' ✓'}
              </strong>
            </td>
            <td style={{ textAlign: 'right' }}>
              <strong>{fmtNum(rows.reduce((s, r) => s + r.pyActual, 0), 2)}</strong>
            </td>
            <td style={{ textAlign: 'right' }}>
              <strong>{fmtNum(rows.reduce((s, r) => s + r.cyActual, 0), 2)}</strong>
            </td>
          </tr>
        </tfoot>
      </table>

      {/* Action bar */}
      {!isSubmitted && (
        <div className="nonbill-actions">
          <button
            className="idd-btn idd-btn--secondary"
            onClick={handleDiscard}
            disabled={saving || submitting || !hasUnsavedChanges}
            title={!hasUnsavedChanges ? 'No unsaved changes' : 'Revert all unsaved edits'}
          >
            Discard Changes
          </button>
          <button
            className="idd-btn idd-btn--primary"
            onClick={handleSaveDraft}
            disabled={saving || submitting || !hasUnsavedChanges}
            title={!hasUnsavedChanges ? 'No unsaved changes' : 'Persist edits as a draft'}
          >
            {saving ? 'Saving…' : 'Save Draft'}
          </button>
          <button
            className="idd-btn idd-btn--submit"
            onClick={handleSubmit}
            disabled={saving || submitting || !exactMatch}
            title={!exactMatch ? `Sum must equal ${fmtNum(totalEffective, 0)} exactly` : 'Lock + finalize for the year'}
          >
            {submitting ? 'Submitting…' : 'Submit'}
          </button>
        </div>
      )}

      {isSubmitted && (
        <div className="idd-readonly-banner" style={{ marginTop: 16 }}>
          <strong>Submitted ✓</strong>
          <span>
            Your non-billable budget for FY {fy} is locked. Contact an admin to reopen if changes are needed.
          </span>
        </div>
      )}
    </div>
  );
}
